import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

/**
 * Loads functional usage when the service center currently assigned to this
 * record is updated or deleted. This ensures the record immediately reflects
 * any methodology or allocation changes (e.g. switching to Manual after the
 * assigned service center is deleted), without requiring a page refresh.
 *
 */
export const loadForCurrentEntityOnServiceCenterChange = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    ec.models.CollectionProducer.getEc$(ec.Name.Predefined.SpaceServiceCenter, {
      waitUntilReady: true,
    }).pipe(
      rxjs.switchMap(serviceCentersEc =>
        serviceCentersEc.plugins.editingPlugin.feature.actions.submit.success
          .observe()
          .pipe(
            rxjs.withLatestFrom(
              plugin.feature.selectors.functionalUsage.asObservable,
            ),
            rxjs.filter(([action, functionalUsage]) => {
              const { updates, deletions } = action.data.response
              const assignedServiceCenterId = functionalUsage?.serviceCenterId

              if (TypeChecker.isNullish(assignedServiceCenterId)) {
                return false
              }

              return (
                updates.some(({ id }) => id === assignedServiceCenterId) ||
                deletions.includes(assignedServiceCenterId)
              )
            }),
            rxjs.map(() =>
              feature.actions.loadForCurrentEntity.request
                .from(
                  '(effect) load for current entity on service center change',
                )
                .create(),
            ),
          ),
      ),
    ),
)
